// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import {
  Sort,
  SortDirection,
  invert as invertDirection,
  sortDirectionSchema,
} from '@/constants/sort';
import { FulfillmentListing } from '@/fulfillment/components/fulfillment-listing';
import {
  FulfillmentSearchListing,
  FulfillmentSortProperty,
  fulfillmentSearchListingSchema,
} from '@/fulfillment/hooks/use-fulfillments';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { DemandScheduleCard } from '@/project/components/demand-schedule-card';
import { FulfillmentBySkuCard } from '@/project/components/fulfillment-by-sku-card';
import { useProjectContext } from '@/project/hooks/use-project-context';
import { Box, Tab, Tabs } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { SyntheticEvent } from 'react';
import { z } from 'zod';

const LOCAL_STORAGE_SORT_KEY = 'project-fulfillment-sort';
const DEFAULT_SORT_PROPERTY: FulfillmentSortProperty = 'foNumber';
const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

const TAB_DEMAND_SCHEDULE = 'demand-schedule';
const TAB_BY_SKU = 'fulfillment-by-sku';
const TAB_ALL_ORDERS = 'fulfillment-orders';
const DEFAULT_TAB = TAB_DEMAND_SCHEDULE;

const projectFulfillmentTabSchema = z.enum([
  TAB_DEMAND_SCHEDULE,
  TAB_BY_SKU,
  TAB_ALL_ORDERS,
]);
type ProjectFulfillmentTab = z.infer<typeof projectFulfillmentTabSchema>;

const sortSchema = z.object({
  property: z.enum(['foNumber', 'status']),
  direction: sortDirectionSchema,
});

const projectFulfillmentSearchSchema = fulfillmentSearchListingSchema
  .omit({ forProjectId: true })
  .extend({
    tab: projectFulfillmentTabSchema.optional(),
  });

export const Route = createFileRoute('/_app/projects/$projectKey/fulfillment')({
  component: ProjectFulfillmentRoute,
  validateSearch: projectFulfillmentSearchSchema,
});

function ProjectFulfillmentRoute() {
  const { project } = useProjectContext();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const selectedTab: ProjectFulfillmentTab = search.tab ?? DEFAULT_TAB;

  const [sortPreference, setSortPreference] = useLocalStorage(
    LOCAL_STORAGE_SORT_KEY,
    sortSchema
  );

  const sort: Sort<FulfillmentSortProperty> = {
    property: search.sort ?? sortPreference?.property ?? DEFAULT_SORT_PROPERTY,
    direction:
      search.direction ?? sortPreference?.direction ?? DEFAULT_SORT_DIRECTION,
  };

  const setSort = (
    property: FulfillmentSortProperty,
    direction: SortDirection
  ) => {
    setSortPreference({ property, direction });
    navigate({ search: (prev) => ({ ...prev, direction, sort: property }) });
  };

  const handleSort = (property: FulfillmentSortProperty) => {
    if (property === sort.property) {
      return setSort(property, invertDirection(sort.direction));
    }
    setSort(property, 'asc');
  };

  const handleTabChange = (
    _event: SyntheticEvent,
    newValue: ProjectFulfillmentTab
  ) => {
    navigate({ search: (prev) => ({ ...prev, tab: newValue }) });
  };

  const searchParameters: FulfillmentSearchListing = {
    page: search.page,
    query: search.query,
    status: search.status,
    subStatuses: search.subStatuses,
    sort: search.sort,
    direction: search.direction,
    forProjectId: project.id,
  };

  return (
    <HardfinTabCardWrapper title="Fulfillment">
      <Box
        sx={{
          display: 'flex',
          paddingLeft: 1,
          flexDirection: 'row',
          '& .Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '& .MuiTab-root': {
            px: 2,
          },
          '& .MuiTab-root.MuiButtonBase-root': {
            ml: 0,
          },
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          aria-label="Project fulfillment views"
        >
          <Tab value={TAB_DEMAND_SCHEDULE} label="Demand schedule" />
          <Tab value={TAB_BY_SKU} label="Fulfillment by SKU" />
          <Tab value={TAB_ALL_ORDERS} label="Fulfillment orders" />
        </Tabs>
      </Box>

      {selectedTab === TAB_DEMAND_SCHEDULE && <DemandScheduleCard />}
      {selectedTab === TAB_BY_SKU && <FulfillmentBySkuCard />}
      {selectedTab === TAB_ALL_ORDERS && (
        <FulfillmentListing
          currentStatus={search.status ?? 'all'}
          searchParameters={searchParameters}
          sort={sort}
          onSort={handleSort}
          navigateFn={navigate}
          forProjectId={project.id}
          initialProjectID={project.key}
          disableProjectChange
          hideChrome
          enableRowExpansion
        />
      )}
    </HardfinTabCardWrapper>
  );
}
